import React from 'react';
import { FlattenSimpleInterpolation } from 'styled-components';
import { LayoutContext } from 'contexts';
import { useHistory } from 'react-router-dom';
import { Icon } from '../Icon';

import {
  StyledTitle,
  StyledTitleWrapper,
  StyledOverAlertWrapper,
  StyledHeaderWrapper,
  StyledPageHeader,
  StyledAside,
  StyledContentWrapper,
  StyledOverTitle,
  StyledBody,
  StyledPageFooter,
  StyledPageContainer,
} from './StyledComponents';

import OverAlert, {
  OverAlertProps,
} from '../PageComponents/AlertComponents/OverAlert';

export interface PageProps {
  title?: string;
  overTitle?: React.ReactNode;
  overAlertVariant?: OverAlertProps['variant'];
  headerExtra?: React.ReactNode;
  headerVerticalAlign?: 'baseline' | 'center' | 'flex-end';
  headerDirection?: 'row' | 'column';
  bodyCss?: FlattenSimpleInterpolation;
  subHeader?: React.ReactNode;
  aside?: React.ReactNode;
  asidePlacement?: 'left' | 'right';
  asideWidth?: React.ReactText;
  asideType?: 'filled' | 'hollow';
  asidePadding?: string;
  footer?: React.ReactNode;
  footerHeight?: number;
  type: 'filled' | 'hollow';
  showBackButton?: boolean;
  goBackUrl?: string;
  fillScreenHeight?: boolean;
  fullTitleWidth?: boolean;
  extraTitleMargin?: boolean;
  backgroundColor?: 'white' | 'transparent';
  toursMobileView?: boolean;
}

const Page: React.FC<PageProps> = ({
  children,
  title,
  overTitle,
  overAlertVariant,
  headerExtra,
  headerVerticalAlign = 'center',
  headerDirection = 'row',
  bodyCss,
  subHeader,
  aside,
  asidePlacement = 'left',
  asideWidth,
  asideType = 'hollow',
  footer,
  footerHeight = 70,
  type,
  showBackButton,
  fillScreenHeight,
  fullTitleWidth,
  extraTitleMargin,
  backgroundColor,
  toursMobileView,
  goBackUrl,
}) => {
  const layout = React.useContext(LayoutContext);
  const { goBack, push } = useHistory();
  const [bodyY, setBodyY] = React.useState(0);

  React.useLayoutEffect(() => {
    if (layout?.scrollContainer && fillScreenHeight) {
      const { y } = layout.scrollContainer.getBoundingClientRect();
      setBodyY(y);
    }
  });

  const contentHeight = React.useMemo(() => {
    const offset = bodyY + (footer ? footerHeight : 0);
    return `calc(100vh - ${offset}px)`;
  }, [bodyY]);

  return (
    <StyledPageContainer $toursMobileView={toursMobileView}>
      {overAlertVariant && (
        <StyledOverAlertWrapper>
          <OverAlert variant={overAlertVariant} />
        </StyledOverAlertWrapper>
      )}

      <StyledHeaderWrapper
        $elevated={type === 'hollow'}
        $toursMobileView={toursMobileView}
      >
        {(title || headerExtra) && (
          <StyledPageHeader
            $verticalAlign={headerVerticalAlign}
            $direction={headerDirection}
            $toursMobileView={toursMobileView}
          >
            <StyledTitleWrapper
              $fullTitleWidth={fullTitleWidth}
              $extraTitleMargin={extraTitleMargin}
              $toursMobileView={toursMobileView}
            >
              {overTitle && <StyledOverTitle>{overTitle}</StyledOverTitle>}
              {showBackButton && (
                <button onClick={goBackUrl ? () => push(goBackUrl) : goBack}>
                  <Icon type="arrowLeftThin" color="black" offset={[0, 2]} />
                </button>
              )}
              <StyledTitle
                level={1}
                hideMargin
                data-cy="page-title"
                $toursMobileView={toursMobileView}
              >
                {title}
              </StyledTitle>
            </StyledTitleWrapper>
            {headerExtra}
          </StyledPageHeader>
        )}
        {subHeader}
      </StyledHeaderWrapper>
      <StyledContentWrapper
        $hasAside={Boolean(aside)}
        $asidePlacement={asidePlacement}
        $asideWidth={asideWidth}
      >
        {aside && (
          <StyledAside
            $height={contentHeight}
            $fillScreenHeight={fillScreenHeight}
            $asidePlacement={asidePlacement}
            $backgroundColor={asideType === 'filled' ? 'white' : 'transparent'}
          >
            {aside}
          </StyledAside>
        )}
        <StyledBody
          cssOverride={bodyCss}
          ref={layout?.setScrollContainer}
          $height={contentHeight}
          $backgroundColor={
            backgroundColor || (type === 'filled' ? 'white' : 'transparent')
          }
          $padding={type === 'filled' ? 0 : 25}
          $fillScreenHeight={fillScreenHeight}
        >
          {layout?.scrollContainer ? children : null}
        </StyledBody>
      </StyledContentWrapper>
      {footer && (
        <StyledPageFooter $height={footerHeight}>{footer}</StyledPageFooter>
      )}
    </StyledPageContainer>
  );
};

export default Page;
